import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, notifyApiError, INR } from "../lib/api";
import { safeCopy } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  LogOut,
  Crown,
  TrendingUp,
  Clock,
  Coins,
  CheckCircle2,
  XCircle,
  Mail,
  Sparkles,
  Download,
  Calendar,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import UnpaidSessionsPanel from "../components/UnpaidSessionsPanel";
import PlayersPortalCard from "@/components/PlayersPortalCard";

function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function hours(mins) {
  const m = Number(mins || 0);
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h}h ${String(r).padStart(2, "0")}m`;
}

// Per-day happy hour schedule — Mon->Sun, matching the backend's
// `happy_hour_schedule` keys exactly.
const HAPPY_HOUR_DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

// Renders a 24-hour "HH:MM" string (the value sent to/from the backend) as a
// 12-hour "h:mm AM/PM" string for display.
function formatTime12h(hhmm) {
  if (!hhmm) return "";
  const [hStr, mStr] = hhmm.split(":");
  const h24 = Number(hStr);
  const m = Number(mStr);
  if (!Number.isFinite(h24) || !Number.isFinite(m)) return "";
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

// Splits a 24-hour "HH:MM" string into the {hour12, minute, period} parts
// the AM/PM selects below are built from.
function hhmmTo12hParts(hhmm) {
  const [hStr, mStr] = (hhmm || "10:00").split(":");
  let h24 = Number(hStr);
  const m = Number(mStr);
  if (!Number.isFinite(h24)) h24 = 10;
  const period = h24 >= 12 ? "PM" : "AM";
  let hour12 = h24 % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12, minute: Number.isFinite(m) ? m : 0, period };
}

// Rebuilds the 24-hour "HH:MM" string (what's stored/sent) from the
// {hour12, minute, period} parts the AM/PM selects produce.
function parts12hToHHMM(hour12, minute, period) {
  let h24 = hour12 % 12;
  if (period === "PM") h24 += 12;
  return `${String(h24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(d) {
  const dt = new Date();
  dt.setDate(dt.getDate() - d);
  return dt.toISOString().slice(0, 10);
}

/** Clickable KPI card — opens a drilldown modal when onClick provided. */
function Kpi({ label, value, icon: Icon, sub, accent = "amber", onClick, hint }) {
  const colors = {
    amber: "from-amber-500/20 to-amber-500/0 border-amber-400/30 text-amber-300",
    emerald: "from-emerald-500/20 to-emerald-500/0 border-emerald-400/30 text-emerald-300",
    blue: "from-blue-500/20 to-blue-500/0 border-blue-400/30 text-blue-300",
    rose: "from-rose-500/20 to-rose-500/0 border-rose-400/30 text-rose-300",
  };
  const isClickable = typeof onClick === "function";
  const Tag = isClickable ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${colors[accent]} bg-[#121a16] p-5 text-left w-full ${
        isClickable
          ? "transition hover:scale-[1.01] hover:shadow-lg hover:shadow-amber-500/10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400/60"
          : ""
      }`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-bold">
            {label}
          </div>
          <div className="text-3xl md:text-4xl font-black mt-2 text-white tabular-nums">
            {value}
          </div>
          {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
        </div>
        <Icon className={`w-6 h-6 ${colors[accent].split(" ").pop()}`} />
      </div>
      {isClickable && (
        <div className="absolute bottom-2 right-3 text-[9px] uppercase tracking-widest font-bold text-gray-500">
          {hint || "Tap for details"}
        </div>
      )}
    </Tag>
  );
}

export default function Owner() {
  const { user, logout } = useAuth();
  // Date range — defaults to last 30 days (IST)
  const [startDate, setStartDate] = useState(daysAgoISO(29));
  const [endDate, setEndDate] = useState(todayISO());
  const [appliedRange, setAppliedRange] = useState({
    start: daysAgoISO(29),
    end: todayISO(),
  });

  const [overview, setOverview] = useState(null);
  const [tables, setTables] = useState([]);
  const [daily, setDaily] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unpaidRefresh, setUnpaidRefresh] = useState(0);
  const [exporting, setExporting] = useState(false);
  // Membership count for this club only (used by the "Total Membership Players" KPI)
  const [memberCount, setMemberCount] = useState(0);

  // Per-table rate management
  const [rateTables, setRateTables] = useState([]); // raw /owner/tables list
  const [rateDrafts, setRateDrafts] = useState({}); // {table_id: "350"}
  const [rateSaving, setRateSaving] = useState({}); // {table_id: bool}

  // Club-wide Happy Hour: one ₹/hr rate + a per-day HH:MM window.
  const [happyRate, setHappyRate] = useState(""); // draft ₹/hr string
  const [happySchedule, setHappySchedule] = useState({}); // {monday: {enabled, start, end}, ...}
  const [happySaving, setHappySaving] = useState(false);

  // Staff credentials (Club logins owned by this cafe)
  const [staff, setStaff] = useState([]);
  const [staffBusy, setStaffBusy] = useState({}); // {id: bool}
  const [staffReveal, setStaffReveal] = useState(null); // {email, new_password}
  // Custom password inputs keyed by staff id, so the owner can set a chosen
  // password (instead of using the auto-generated reset).
  const [staffCustomPwd, setStaffCustomPwd] = useState({}); // {id: string}

  // Drilldowns
  const [revenueDrill, setRevenueDrill] = useState(false);
  const [playtimeDrill, setPlaytimeDrill] = useState(false);

  // Email-report modal
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: "", note: "", channel: "gmail" });
  const [emailing, setEmailing] = useState(false);

  // Sessions table filter — which table's sessions to show below.
  // "" means "All tables".
  const [selectedTableId, setSelectedTableId] = useState("");

  const qs = useMemo(() => {
    const s = appliedRange.start;
    const e = appliedRange.end;
    return `start=${s}&end=${e}`;
  }, [appliedRange]);

  const rangeDays = useMemo(() => {
    try {
      const s = new Date(appliedRange.start);
      const e = new Date(appliedRange.end);
      return Math.max(1, Math.round((e - s) / 86400000) + 1);
    } catch {
      return 1;
    }
  }, [appliedRange]);
  // rangeDays is kept for the "X days" label in headers
  void rangeDays;

  const refresh = async () => {
    setLoading(true);
    try {
      const [o, t, dDaily, s, rt, st, settingsRes] = await Promise.all([
        api.get(`/analytics/overview?${qs}`),
        api.get(`/analytics/tables?${qs}`),
        api.get(`/analytics/daily?${qs}`),
        api.get(`/analytics/sessions?${qs}&limit=5000`),
        api.get(`/owner/tables`),
        api.get(`/owner/staff`),
        api.get(`/settings`),
      ]);
      setOverview(o.data);
      setTables(t.data);
      setDaily(dDaily.data);
      setSessions(s.data);
      setRateTables(rt.data || []);
      setStaff(st.data || []);
      // Seed the Happy Hour draft from saved settings. Any day missing from
      // the saved schedule (e.g. very first load on an older cafe) falls
      // back to a sensible disabled default so the row still renders.
      const savedSettings = settingsRes.data || {};
      setHappyRate(
        savedSettings.rate_happy_hour != null ? String(savedSettings.rate_happy_hour) : "",
      );
      const savedSchedule = savedSettings.happy_hour_schedule || {};
      const seededSchedule = {};
      HAPPY_HOUR_DAYS.forEach(({ key }) => {
        const d = savedSchedule[key];
        seededSchedule[key] = {
          enabled: d?.enabled !== false && !!d,
          start: d?.start || "10:00",
          end: d?.end || "14:00",
        };
      });
      setHappySchedule(seededSchedule);
      // Seed drafts from server-side rates so the inputs reflect saved values.
      // Each table has THREE independent rate fields the owner can edit:
      //   - member         (hourly_rate_member)
      //   - non_member     (hourly_rate_non_member)
      //   - flat           (hourly_rate)        — legacy / "everyone" override
      // We only surface Member + Cross-Club + Non-Member in the UI; the flat rate is kept
      // intact when all per-type fields are blank.
      const drafts = {};
      (rt.data || []).forEach((row) => {
        drafts[row.id] = {
          member: row.hourly_rate_member != null ? String(row.hourly_rate_member) : "",
          cross_club_member: row.hourly_rate_cross_club_member != null ? String(row.hourly_rate_cross_club_member) : "",
          non_member: row.hourly_rate_non_member != null ? String(row.hourly_rate_non_member) : "",
        };
      });
      setRateDrafts(drafts);
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setLoading(false);
    }
  };

  const saveTableRate = async (tableId) => {
    const draft = rateDrafts[tableId] || { member: "", cross_club_member: "", non_member: "" };
    const parse = (raw) => {
      const v = (raw ?? "").toString().trim();
      if (v === "") return null;
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0) return undefined; // invalid
      return n;
    };
    const member = parse(draft.member);
    const crossClub = parse(draft.cross_club_member);
    const nonMember = parse(draft.non_member);
    if (member === undefined || crossClub === undefined || nonMember === undefined) {
      toast.error("Rates must be positive numbers");
      return;
    }
    setRateSaving((prev) => ({ ...prev, [tableId]: true }));
    try {
      const { data } = await api.patch(`/owner/tables/${tableId}/rate`, {
        hourly_rate_member: member,
        hourly_rate_cross_club_member: crossClub,
        hourly_rate_non_member: nonMember,
        // Keep the legacy flat-rate override null when split rates are used.
        // The backend's resolver prefers split rates over the flat one anyway,
        // but clearing it here keeps the per-table doc clean & predictable.
        hourly_rate: null,
      });
      setRateTables((prev) => prev.map((t) => (t.id === tableId ? data : t)));
      if (member === null && crossClub === null && nonMember === null) {
        toast.success(`${data.name}: rates cleared — using club defaults`);
      } else {
        const bits = [];
        if (member !== null) bits.push(`Member ₹${member}`);
        if (crossClub !== null) bits.push(`Cross-Club ₹${crossClub}`);
        if (nonMember !== null) bits.push(`Non-Member ₹${nonMember}`);
        toast.success(`${data.name}: ${bits.join(" · ")} /hr saved`);
      }
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setRateSaving((prev) => ({ ...prev, [tableId]: false }));
    }
  };

  const updateHappyDay = (key, patch) => {
    setHappySchedule((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || { enabled: true, start: "10:00", end: "14:00" }), ...patch },
    }));
  };

  const saveHappyHours = async () => {
    const rate = Number(happyRate);
    if (!Number.isFinite(rate) || rate < 0) {
      toast.error("Happy hour rate must be a positive number");
      return;
    }
    for (const { key, label } of HAPPY_HOUR_DAYS) {
      const d = happySchedule[key];
      if (d?.enabled && (!d.start || !d.end)) {
        toast.error(`${label}: pick both a start and end time, or turn the day off`);
        return;
      }
      if (d?.enabled && d.start === d.end) {
        toast.error(`${label}: start and end time can't be the same`);
        return;
      }
    }
    setHappySaving(true);
    try {
      const { data } = await api.put(`/settings`, {
        rate_happy_hour: rate,
        happy_hour_schedule: happySchedule,
      });
      setHappyRate(data.rate_happy_hour != null ? String(data.rate_happy_hour) : "");
      const saved = data.happy_hour_schedule || {};
      const reseeded = {};
      HAPPY_HOUR_DAYS.forEach(({ key }) => {
        const d = saved[key];
        reseeded[key] = {
          enabled: d?.enabled !== false && !!d,
          start: d?.start || "10:00",
          end: d?.end || "14:00",
        };
      });
      setHappySchedule(reseeded);
      toast.success("Happy hour schedule saved");
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setHappySaving(false);
    }
  };

  const resetStaffPassword = async (staffId) => {
    setStaffBusy((prev) => ({ ...prev, [staffId]: true }));
    try {
      const { data } = await api.post(`/owner/staff/${staffId}/reset-password`, null, { retryOnNetworkError: true });
      setStaffReveal({ email: data.email, new_password: data.new_password });
      toast.success("Staff password reset — share the new password securely");
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setStaffBusy((prev) => ({ ...prev, [staffId]: false }));
    }
  };

  const setStaffCustomPassword = async (staffId) => {
    const pwd = (staffCustomPwd[staffId] || "").trim();
    if (pwd.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setStaffBusy((prev) => ({ ...prev, [staffId]: true }));
    try {
      const { data } = await api.post(`/owner/staff/${staffId}/set-password`, {
        new_password: pwd,
      }, { retryOnNetworkError: true });
      toast.success(
        `Staff password updated — ${data.email} can now sign in with the new password`,
      );
      setStaffCustomPwd((prev) => ({ ...prev, [staffId]: "" }));
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setStaffBusy((prev) => ({ ...prev, [staffId]: false }));
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs]);

  const applyRange = () => {
    if (!startDate || !endDate) {
      toast.error("Pick both dates");
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      toast.error("End date must be on/after start date");
      return;
    }
    setAppliedRange({ start: startDate, end: endDate });
  };

  const quickRange = (d) => {
    const e = todayISO();
    const s = daysAgoISO(d - 1);
    setStartDate(s);
    setEndDate(e);
    setAppliedRange({ start: s, end: e });
  };

  const fetchReportBlob = async () => {
    // Use axios so we share the same cookie/credential handling + retry
    // interceptor as the rest of the app. Plain `fetch` was returning
    // mixed results inside the sandboxed preview iframe.
    const resp = await api.get(`/analytics/export.xlsx?${qs}`, {
      responseType: "blob",
    });
    return new Blob([resp.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const blob = await fetchReportBlob();
      const fname = `cue-lounge-report-${appliedRange.start}_to_${appliedRange.end}.xlsx`;
      const objUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objUrl;
      link.download = fname;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Fallback for iframe sandboxes that ignore programmatic <a download>:
      // open the blob in a new tab so the user can save it from there.
      setTimeout(() => {
        try {
          window.open(objUrl, "_blank", "noopener,noreferrer");
        } catch (_err) {
          /* iframe may block window.open — the <a download> already fired */
        }
        setTimeout(() => URL.revokeObjectURL(objUrl), 8000);
      }, 250);
      toast.success("Excel ready — check your downloads");
    } catch (e) {
      notifyApiError(toast, e, "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const shareReport = async () => {
    setEmailing(true);
    try {
      const fname = `cue-lounge-report-${appliedRange.start}_to_${appliedRange.end}.xlsx`;
      const subject = `Cue Lounge Report (${appliedRange.start} → ${appliedRange.end})`;
      const bodyLines = [
        `Cue Lounge — Owner Report`,
        `Window: ${appliedRange.start} → ${appliedRange.end}`,
        `Revenue Collected: ${INR(totalCollected)}`,
        `Total Play Time: ${hours(totalMinutes)}`,
        `Sessions: ${overview?.total_sessions || 0}`,
      ];
      if (emailForm.note?.trim()) bodyLines.push("", `Note: ${emailForm.note.trim()}`);
      bodyLines.push("", `(Excel report attached — file: ${fname})`);
      const body = bodyLines.join("\n");
      const to = (emailForm.to || "").trim();

      // 1) Open the chosen channel FIRST while we still have the user gesture.
      const channel = emailForm.channel || "gmail";
      const u = encodeURIComponent;
      let openUrl = "";
      if (channel === "gmail") {
        openUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${u(to)}&su=${u(subject)}&body=${u(body)}`;
      } else if (channel === "outlook") {
        openUrl = `https://outlook.office.com/mail/deeplink/compose?to=${u(to)}&subject=${u(subject)}&body=${u(body)}`;
      } else if (channel === "yahoo") {
        openUrl = `https://compose.mail.yahoo.com/?to=${u(to)}&subject=${u(subject)}&body=${u(body)}`;
      } else if (channel === "whatsapp") {
        openUrl = `https://wa.me/?text=${u(`${subject}\n\n${body}`)}`;
      } else {
        openUrl = `mailto:${u(to)}?subject=${u(subject)}&body=${u(body)}`;
      }
      const newWin = window.open(openUrl, "_blank", "noopener,noreferrer");
      if (!newWin) {
        toast.error(
          "Browser blocked the new tab. Please allow popups for this site and try again."
        );
      }

      // 2) Download the Excel (use axios so the auth cookie/header is set the same way as everywhere else).
      toast.message("Building Excel…");
      const resp = await api.get(`/analytics/export.xlsx?${qs}`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      link.href = objUrl;
      link.download = fname;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objUrl), 4000);

      toast.success(
        `Excel downloaded → attach "${fname}" in the ${channel} tab and send`
      );
      setEmailOpen(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("share report failed:", e);
      toast.error(formatApiError(e) || e.message || "Share failed");
    } finally {
      setEmailing(false);
    }
  };

  const totalCollected = overview?.revenue_collected || 0;
  const totalMinutes = overview?.total_minutes || 0;

  // Sorted views for drilldowns
  const tablesByRevenue = useMemo(
    () => [...tables].sort((a, b) => b.revenue_collected - a.revenue_collected),
    [tables]
  );
  const tablesByMinutes = useMemo(
    () => [...tables].sort((a, b) => b.total_minutes - a.total_minutes),
    [tables]
  );

  // Sessions filtered by the currently-selected table.
  const filteredSessions = useMemo(() => {
    if (!selectedTableId) return sessions;
    return sessions.filter((s) => s.table_id === selectedTableId);
  }, [sessions, selectedTableId]);

  const selectedTableName = useMemo(() => {
    if (!selectedTableId) return "All tables";
    const t = tables.find((x) => x.table_id === selectedTableId);
    return t?.table_name || "—";
  }, [selectedTableId, tables]);

  return (
    <div className="min-h-screen bg-[#0a0f0d] text-white">
      {/* Header */}
      <header className="sticky top-0 z-30 w-full px-4 sm:px-6 lg:px-8 pt-4">
        <div className="w-full flex items-center justify-between bg-black/50 backdrop-blur-xl border border-white/10 px-5 py-3 rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.45)]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center text-black font-black shadow-lg shadow-amber-500/30">
              <Crown className="w-4 h-4" />
            </div>
            <div>
              <div className="text-lg font-black tracking-tight text-white">
                Owner Console
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400/80">
                {user?.cafe?.name || "Cue Lounge"}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={logout}
              data-testid="logout-btn"
              className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Date range bar */}
        <section
          data-testid="date-range-bar"
          className="bg-[#121a16] border border-white/10 rounded-2xl p-4 sm:p-5 flex flex-col lg:flex-row lg:items-end gap-3 lg:gap-4"
        >
          <div className="flex items-center gap-2 text-amber-300">
            <Calendar className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.25em] font-bold">
              Date Range
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="flex-1 space-y-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">
                Start
              </Label>
              <Input
                data-testid="start-date-input"
                type="date"
                value={startDate}
                max={endDate || todayISO()}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div className="flex-1 space-y-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">
                End
              </Label>
              <Input
                data-testid="end-date-input"
                type="date"
                value={endDate}
                min={startDate}
                max={todayISO()}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <Button
              onClick={applyRange}
              data-testid="apply-range-btn"
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest text-xs"
            >
              {loading ? "Loading…" : "Apply"}
            </Button>
            <Button
              onClick={exportExcel}
              disabled={exporting || loading}
              data-testid="export-excel-btn"
              className="bg-emerald-500 hover:bg-emerald-600 text-black font-black uppercase tracking-widest text-xs"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              {exporting ? "Exporting…" : "Excel"}
            </Button>
          </div>
        </section>

        {/* KPI row */}
        <section
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          data-testid="owner-kpis"
        >
          <Kpi
            label="Revenue Collected"
            value={INR(totalCollected)}
            sub={`Billed ${INR(overview?.revenue_billed || 0)} · Outstanding ${INR(overview?.outstanding || 0)}`}
            icon={Coins}
            accent="amber"
            onClick={() => setRevenueDrill(true)}
            hint="Tap for per-table"
          />
          <Kpi
            label="Total Play Time"
            value={hours(totalMinutes)}
            sub={`${totalMinutes} minutes across ${overview?.total_sessions || 0} sessions`}
            icon={Clock}
            accent="emerald"
            onClick={() => setPlaytimeDrill(true)}
            hint="Tap for per-table"
          />
          <Kpi
            label="Sessions"
            value={overview?.total_sessions || 0}
            sub={`Paid ${overview?.paid_sessions || 0} · Unpaid ${overview?.unpaid_sessions || 0}`}
            icon={TrendingUp}
            accent="blue"
          />
          <Kpi
            label="Total Membership Players"
            value={memberCount}
            sub="Only your club"
            icon={Sparkles}
            accent="rose"
          />
        </section>

        {/* Quick jump to the Unpaid Sessions panel at the bottom of the page */}
        <button
          type="button"
          data-testid="unpaid-quick-jump"
          onClick={() => {
            const el = document.getElementById("unpaid-sessions-anchor");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className={
            "w-full sm:w-auto self-start inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-bold transition border " +
            ((overview?.unpaid_sessions || 0) > 0
              ? "border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20"
              : "border-emerald-500/30 bg-emerald-500/5 text-emerald-200/90 hover:bg-emerald-500/10")
          }
        >
          <XCircle className="h-3.5 w-3.5" />
          {(overview?.unpaid_sessions || 0) > 0
            ? `${overview.unpaid_sessions} Unpaid Session${overview.unpaid_sessions === 1 ? "" : "s"}`
            : "Unpaid Sessions"}
          <span className="font-black">· View</span>
        </button>

        {/* Table Light Wiring moved to Platform Console for centralized hardware management */}

        {/* Per-table hourly rates (owner editable) */}
        <section
          data-testid="table-rates-section"
          className="bg-[#121a16] border border-white/10 rounded-2xl p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm uppercase tracking-[0.25em] text-gray-300 font-bold">
                Table Hourly Rates
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Set Member, Cross-Club Member &amp; Non-Member ₹/hour per table.
                Leave a field blank to use the club&apos;s default rate (incl.
                Happy Hour) for that player type.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3">
            {rateTables.map((t) => {
              const draft = rateDrafts[t.id] || { member: "", cross_club_member: "", non_member: "" };
              const hasMember = t.hourly_rate_member != null;
              const hasCrossClub = t.hourly_rate_cross_club_member != null;
              const hasNonMember = t.hourly_rate_non_member != null;
              const anyPerType = hasMember || hasCrossClub || hasNonMember;
              const hasFlat = t.hourly_rate != null && !anyPerType;
              const badge = anyPerType
                ? "Custom"
                : hasFlat
                  ? "Flat"
                  : "Default";
              const badgeCls = anyPerType
                ? "bg-amber-400/15 text-amber-200 border-amber-400/40"
                : hasFlat
                  ? "bg-emerald-400/15 text-emerald-200 border-emerald-400/40"
                  : "bg-white/5 text-gray-400 border-white/10";
              return (
                <div
                  key={t.id}
                  data-testid={`rate-card-${t.id}`}
                  className="bg-black/30 border border-white/10 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-black uppercase tracking-wider text-white text-sm">
                      {t.name}
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border ${badgeCls}`}
                    >
                      {badge}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                    {/* MEMBER */}
                    <label className="block">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
                          Club Member
                        </span>
                        {hasMember && (
                          <span className="text-[9px] uppercase tracking-widest text-amber-400/70 font-bold">
                            Custom
                          </span>
                        )}
                      </div>
                      <div className="flex items-center bg-black/40 border border-white/10 rounded-md focus-within:ring-2 focus-within:ring-amber-400/60">
                        <span className="text-amber-300 font-black pl-2.5 pr-1">₹</span>
                        <input
                          data-testid={`rate-input-member-${t.id}`}
                          type="number"
                          min="0"
                          step="10"
                          placeholder="default"
                          value={draft.member}
                          onChange={(e) =>
                            setRateDrafts((prev) => ({
                              ...prev,
                              [t.id]: { ...(prev[t.id] || {}), member: e.target.value },
                            }))
                          }
                          className="w-full bg-transparent text-white py-2 pr-2 text-sm outline-none tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="text-[9px] uppercase tracking-widest text-gray-500 pr-2">
                          /hr
                        </span>
                      </div>
                    </label>

                    {/* CROSS-CLUB MEMBER */}
                    <label className="block">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-widest text-sky-300 font-bold">
                          Cross-Club
                        </span>
                        {hasCrossClub && (
                          <span className="text-[9px] uppercase tracking-widest text-sky-400/70 font-bold">
                            Custom
                          </span>
                        )}
                      </div>
                      <div className="flex items-center bg-black/40 border border-white/10 rounded-md focus-within:ring-2 focus-within:ring-sky-400/60">
                        <span className="text-sky-300 font-black pl-2.5 pr-1">₹</span>
                        <input
                          data-testid={`rate-input-crossclub-${t.id}`}
                          type="number"
                          min="0"
                          step="10"
                          placeholder="default"
                          value={draft.cross_club_member}
                          onChange={(e) =>
                            setRateDrafts((prev) => ({
                              ...prev,
                              [t.id]: { ...(prev[t.id] || {}), cross_club_member: e.target.value },
                            }))
                          }
                          className="w-full bg-transparent text-white py-2 pr-2 text-sm outline-none tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="text-[9px] uppercase tracking-widest text-gray-500 pr-2">
                          /hr
                        </span>
                      </div>
                    </label>

                    {/* NON-MEMBER */}
                    <label className="block">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-widest text-gray-300 font-bold">
                          Non-Member
                        </span>
                        {hasNonMember && (
                          <span className="text-[9px] uppercase tracking-widest text-emerald-300/80 font-bold">
                            Custom
                          </span>
                        )}
                      </div>
                      <div className="flex items-center bg-black/40 border border-white/10 rounded-md focus-within:ring-2 focus-within:ring-emerald-400/60">
                        <span className="text-emerald-300 font-black pl-2.5 pr-1">₹</span>
                        <input
                          data-testid={`rate-input-nonmember-${t.id}`}
                          type="number"
                          min="0"
                          step="10"
                          placeholder="default"
                          value={draft.non_member}
                          onChange={(e) =>
                            setRateDrafts((prev) => ({
                              ...prev,
                              [t.id]: { ...(prev[t.id] || {}), non_member: e.target.value },
                            }))
                          }
                          className="w-full bg-transparent text-white py-2 pr-2 text-sm outline-none tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="text-[9px] uppercase tracking-widest text-gray-500 pr-2">
                          /hr
                        </span>
                      </div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      data-testid={`rate-clear-${t.id}`}
                      onClick={() =>
                        setRateDrafts((prev) => ({
                          ...prev,
                          [t.id]: { member: "", cross_club_member: "", non_member: "" },
                        }))
                      }
                      className="text-[10px] uppercase tracking-widest font-bold text-gray-400 hover:text-gray-200 border border-white/10 hover:border-white/30 px-2.5 py-1.5 rounded-md"
                    >
                      Clear
                    </button>
                    <Button
                      data-testid={`rate-save-${t.id}`}
                      onClick={() => saveTableRate(t.id)}
                      disabled={!!rateSaving[t.id]}
                      className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest text-[10px] px-4"
                    >
                      {rateSaving[t.id] ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </div>
              );
            })}
            {rateTables.length === 0 && (
              <div className="col-span-full text-center text-gray-500 text-sm py-6">
                No tables yet — ask the platform admin to add tables to your
                cafe.
              </div>
            )}
          </div>
        </section>

        {/* Happy Hour schedule (owner editable) — one ₹/hr rate club-wide,
            plus an independent HH:MM window for every day of the week. */}
        <section
          data-testid="happy-hour-section"
          className="bg-[#121a16] border border-white/10 rounded-2xl p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm uppercase tracking-[0.25em] text-gray-300 font-bold flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-300" />
                Happy Hour Schedule
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Set the Happy Hour ₹/hour rate and turn it on for a specific
                time window on each day of the week. Days left off stay at
                the normal rate all day.
              </p>
            </div>
          </div>

          <label className="block max-w-xs mb-4">
            <span className="text-[10px] uppercase tracking-widest text-gray-300 font-bold">
              Happy Hour Rate
            </span>
            <div className="flex items-center bg-black/40 border border-white/10 rounded-md focus-within:ring-2 focus-within:ring-amber-400/60 mt-1">
              <span className="text-amber-300 font-black pl-2.5 pr-1">₹</span>
              <input
                data-testid="happy-hour-rate-input"
                type="number"
                min="0"
                step="10"
                placeholder="e.g. 200"
                value={happyRate}
                onChange={(e) => setHappyRate(e.target.value)}
                className="w-full bg-transparent text-white py-2 pr-2 text-sm outline-none tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span className="text-[9px] uppercase tracking-widest text-gray-500 pr-2">
                /hr
              </span>
            </div>
          </label>

          <div className="space-y-2">
            {HAPPY_HOUR_DAYS.map(({ key, label }) => {
              const d = happySchedule[key] || { enabled: false, start: "10:00", end: "14:00" };
              return (
                <div
                  key={key}
                  data-testid={`happy-hour-row-${key}`}
                  className="flex flex-wrap items-center gap-3 bg-black/30 border border-white/10 rounded-xl px-4 py-3"
                >
                  <label className="flex items-center gap-2 w-32 shrink-0 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid={`happy-hour-enabled-${key}`}
                      checked={!!d.enabled}
                      onChange={(e) => updateHappyDay(key, { enabled: e.target.checked })}
                      className="h-4 w-4 rounded border-white/20 bg-black/40 accent-amber-500"
                    />
                    <span className="text-xs font-bold uppercase tracking-wider text-white">
                      {label}
                    </span>
                  </label>

                  <div className="flex items-center gap-2 flex-wrap">
                    {/* START time — Hour / Minute / AM-PM */}
                    <div className="flex items-center gap-1">
                      <select
                        data-testid={`happy-hour-start-hour-${key}`}
                        value={hhmmTo12hParts(d.start).hour12}
                        disabled={!d.enabled}
                        onChange={(e) => {
                          const { minute, period } = hhmmTo12hParts(d.start);
                          updateHappyDay(key, {
                            start: parts12hToHHMM(Number(e.target.value), minute, period),
                          });
                        }}
                        className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white outline-none focus:ring-2 focus:ring-amber-400/60 disabled:opacity-40 cursor-pointer"
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                      <span className="text-gray-500 text-sm">:</span>
                      <select
                        data-testid={`happy-hour-start-minute-${key}`}
                        value={hhmmTo12hParts(d.start).minute}
                        disabled={!d.enabled}
                        onChange={(e) => {
                          const { hour12, period } = hhmmTo12hParts(d.start);
                          updateHappyDay(key, {
                            start: parts12hToHHMM(hour12, Number(e.target.value), period),
                          });
                        }}
                        className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white outline-none focus:ring-2 focus:ring-amber-400/60 disabled:opacity-40 cursor-pointer"
                      >
                        {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
                          <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                        ))}
                      </select>
                      <select
                        data-testid={`happy-hour-start-period-${key}`}
                        value={hhmmTo12hParts(d.start).period}
                        disabled={!d.enabled}
                        onChange={(e) => {
                          const { hour12, minute } = hhmmTo12hParts(d.start);
                          updateHappyDay(key, {
                            start: parts12hToHHMM(hour12, minute, e.target.value),
                          });
                        }}
                        className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white outline-none focus:ring-2 focus:ring-amber-400/60 disabled:opacity-40 cursor-pointer"
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>

                    <span className="text-[10px] uppercase tracking-widest text-gray-500">
                      to
                    </span>

                    {/* END time — Hour / Minute / AM-PM */}
                    <div className="flex items-center gap-1">
                      <select
                        data-testid={`happy-hour-end-hour-${key}`}
                        value={hhmmTo12hParts(d.end).hour12}
                        disabled={!d.enabled}
                        onChange={(e) => {
                          const { minute, period } = hhmmTo12hParts(d.end);
                          updateHappyDay(key, {
                            end: parts12hToHHMM(Number(e.target.value), minute, period),
                          });
                        }}
                        className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white outline-none focus:ring-2 focus:ring-amber-400/60 disabled:opacity-40 cursor-pointer"
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                      <span className="text-gray-500 text-sm">:</span>
                      <select
                        data-testid={`happy-hour-end-minute-${key}`}
                        value={hhmmTo12hParts(d.end).minute}
                        disabled={!d.enabled}
                        onChange={(e) => {
                          const { hour12, period } = hhmmTo12hParts(d.end);
                          updateHappyDay(key, {
                            end: parts12hToHHMM(hour12, Number(e.target.value), period),
                          });
                        }}
                        className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white outline-none focus:ring-2 focus:ring-amber-400/60 disabled:opacity-40 cursor-pointer"
                      >
                        {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
                          <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                        ))}
                      </select>
                      <select
                        data-testid={`happy-hour-end-period-${key}`}
                        value={hhmmTo12hParts(d.end).period}
                        disabled={!d.enabled}
                        onChange={(e) => {
                          const { hour12, minute } = hhmmTo12hParts(d.end);
                          updateHappyDay(key, {
                            end: parts12hToHHMM(hour12, minute, e.target.value),
                          });
                        }}
                        className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white outline-none focus:ring-2 focus:ring-amber-400/60 disabled:opacity-40 cursor-pointer"
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </div>

                  {d.enabled && d.start && d.end && (
                    <span
                      data-testid={`happy-hour-12h-${key}`}
                      className="text-[10px] uppercase tracking-widest text-amber-300/80 font-bold"
                    >
                      {formatTime12h(d.start)} – {formatTime12h(d.end)}
                    </span>
                  )}

                  {!d.enabled && (
                    <span className="text-[10px] uppercase tracking-widest text-gray-500">
                      Off — normal rate all day
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-end mt-4">
            <Button
              data-testid="happy-hour-save"
              onClick={saveHappyHours}
              disabled={happySaving}
              className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest text-[10px] px-4"
            >
              {happySaving ? "Saving…" : "Save Happy Hour Schedule"}
            </Button>
          </div>
        </section>

        {/* Platform Fee Tracker — read-only summary of what this cafe owes
            the platform for the current billing cycle. Sits below the
            tables section so the owner sees their floor first. */}
        {user?.subscription && (
          <section
            data-testid="platform-fee-tracker"
            className="bg-[#121a16] border border-amber-400/30 rounded-2xl p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm uppercase tracking-[0.25em] text-amber-200 font-bold">
                  Platform Fee Tracker
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Your current platform subscription cycle.
                </p>
              </div>
              <span
                className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border ${
                  user.subscription.status === "active"
                    ? "border-emerald-400/40 text-emerald-200 bg-emerald-400/10"
                    : "border-red-400/40 text-red-200 bg-red-400/10"
                }`}
              >
                {user.subscription.status}
              </span>
            </div>
            {(() => {
              const cycle = Number(user.subscription.billing_cycle_days || 30);
              const rate = Number(user.cafe?.daily_rate || 0);
              const tables = Number(user.cafe?.tables_count || 0);
              const totalCost = +(rate * tables * cycle).toFixed(2);
              const dateOrDash = (raw) =>
                raw
                  ? new Date(raw).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                    })
                  : "—";
              return (
                <div
                  className="grid grid-cols-2 lg:grid-cols-5 gap-3"
                  data-testid="platform-fee-grid"
                >
                  <FeeTile
                    label="Subscription Started"
                    v={dateOrDash(user.subscription.billing_period_start)}
                  />
                  <FeeTile
                    label="Ending On"
                    v={dateOrDash(user.subscription.billing_period_end)}
                    sub={
                      user.subscription.status === "active" &&
                      user.subscription.days_remaining > 0
                        ? `${user.subscription.days_remaining} day(s) left`
                        : null
                    }
                  />
                  <FeeTile label="Days Billed" v={`${cycle} days`} />
                  <FeeTile
                    label="Per Table"
                    v={INR(+(rate * cycle).toFixed(2))}
                    sub={`${tables}×₹${rate}/day`}
                  />
                  <FeeTile
                    label="Total Cost"
                    v={INR(totalCost)}
                    accent="amber"
                  />
                </div>
              );
            })()}
          </section>
        )}

        {/* Staff Credentials — owner can reset the Club (admin) password */}
        <section
          data-testid="staff-credentials-section"
          className="bg-[#121a16] border border-white/10 rounded-2xl p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm uppercase tracking-[0.25em] text-gray-300 font-bold">
                Staff Credentials
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                The Club login your staff use on the tablet. If they forget the
                password, reset it here and share the new one with them.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {staff.length === 0 && (
              <div className="text-sm text-gray-500 py-3">No staff accounts yet.</div>
            )}
            {staff.map((u) => (
              <div
                key={u.id}
                data-testid={`staff-row-${u.id}`}
                className="bg-black/30 border border-white/10 rounded-xl p-3 sm:p-4 flex flex-col gap-3"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
                      Login email
                    </div>
                    <div className="font-mono text-white text-sm break-all">
                      {u.email}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">
                      {u.name || "Staff"} · role: club
                    </div>
                  </div>
                  <Button
                    onClick={() => resetStaffPassword(u.id)}
                    disabled={!!staffBusy[u.id]}
                    data-testid={`staff-reset-${u.id}`}
                    className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest text-xs"
                  >
                    {staffBusy[u.id] ? "Working…" : "Generate random"}
                  </Button>
                </div>

                {/* Owner-chosen password — saved as-is so the staff can sign in
                    with this exact password via the Club role on /login. */}
                <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-white/5">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">
                      Or set a custom password (min 6 chars)
                    </div>
                    <Input
                      type="text"
                      placeholder="new password"
                      data-testid={`staff-custom-pwd-${u.id}`}
                      value={staffCustomPwd[u.id] || ""}
                      onChange={(e) =>
                        setStaffCustomPwd((prev) => ({ ...prev, [u.id]: e.target.value }))
                      }
                      className="bg-black/40 border-white/10 text-white font-mono"
                    />
                  </div>
                  <Button
                    onClick={() => setStaffCustomPassword(u.id)}
                    disabled={!!staffBusy[u.id] || (staffCustomPwd[u.id] || "").length < 6}
                    data-testid={`staff-set-pwd-${u.id}`}
                    className="bg-emerald-500 hover:bg-emerald-600 text-black font-black uppercase tracking-widest text-xs self-end"
                  >
                    {staffBusy[u.id] ? "Saving…" : "Save password"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Per-table breakdown */}
        <section className="bg-[#121a16] border border-white/10 rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-sm uppercase tracking-[0.25em] text-gray-300 font-bold">
              Per-Table Breakdown
            </h3>
            <span className="text-xs text-gray-500">
              {appliedRange.start} → {appliedRange.end}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tables-breakdown">
              <thead className="text-gray-500 text-[10px] uppercase tracking-[0.2em] bg-black/30">
                <tr>
                  <th className="text-left px-5 py-3">Table</th>
                  <th className="text-right px-5 py-3">Sessions</th>
                  <th className="text-right px-5 py-3">Total Time</th>
                  <th className="text-right px-5 py-3">Billed</th>
                  <th className="text-right px-5 py-3">Collected</th>
                </tr>
              </thead>
              <tbody>
                {tables.map((t) => {
                  const isActive = selectedTableId === t.table_id;
                  return (
                    <tr
                      key={t.table_id}
                      className={`border-t border-white/5 cursor-pointer transition ${
                        isActive
                          ? "bg-amber-400/10 hover:bg-amber-400/15"
                          : "hover:bg-white/[0.03]"
                      }`}
                      data-testid={`row-${t.table_id}`}
                      onClick={() => {
                        setSelectedTableId(isActive ? "" : t.table_id);
                        // Smooth-scroll to the sessions section so the filtered
                        // result is immediately visible.
                        setTimeout(() => {
                          const el = document.getElementById("sessions-section");
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }, 30);
                      }}
                      title={isActive ? "Click to clear filter" : "Click to view this table's sessions"}
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-1.5 h-6 rounded-full ${isActive ? "bg-amber-400" : "bg-white/10"}`} />
                          <div>
                            <div className="font-bold text-white">{t.table_name}</div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500">
                              {t.status}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right font-mono tabular-nums">
                        {t.sessions}
                      </td>
                      <td className="px-5 py-3 text-right font-mono tabular-nums text-emerald-300">
                        {hours(t.total_minutes)}
                      </td>
                      <td className="px-5 py-3 text-right font-mono tabular-nums text-gray-300">
                        {INR(t.revenue_billed)}
                      </td>
                      <td className="px-5 py-3 text-right font-mono tabular-nums text-amber-300 font-bold">
                        {INR(t.revenue_collected)}
                      </td>
                    </tr>
                  );
                })}
                {tables.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center text-gray-500 py-8 text-sm"
                    >
                      No data in this window
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-2 text-[11px] text-gray-500 border-t border-white/5">
            Tip: click a row to view that table&apos;s sessions below.
          </div>
        </section>

        {/* Players Portal — moved to the end of the page */}

        {/* Sessions log */}
        <section
          id="sessions-section"
          className="bg-[#121a16] border border-white/10 rounded-2xl overflow-hidden"
        >
          <div className="p-5 border-b border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-sm uppercase tracking-[0.25em] text-gray-300 font-bold">
                Sessions &amp; Receipts
              </h3>
              <p className="text-[11px] text-gray-500 mt-1">
                Showing {filteredSessions.length} record{filteredSessions.length === 1 ? "" : "s"} · {selectedTableName}
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
                Table
              </span>
              <select
                data-testid="sessions-table-filter"
                value={selectedTableId}
                onChange={(e) => setSelectedTableId(e.target.value)}
                className="bg-black/50 border border-white/10 text-white text-xs uppercase tracking-wider rounded-md px-3 py-2 outline-none focus:border-amber-400/60 focus:ring-2 focus:ring-amber-400/30 min-w-[180px] cursor-pointer"
              >
                <option value="">All tables</option>
                {tables.map((t) => (
                  <option key={t.table_id} value={t.table_id}>
                    {t.table_name} · {t.sessions} session{t.sessions === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
              {selectedTableId && (
                <button
                  type="button"
                  onClick={() => setSelectedTableId("")}
                  data-testid="clear-table-filter"
                  className="text-[10px] uppercase tracking-widest font-bold text-amber-300 hover:text-amber-200 border border-amber-400/40 hover:border-amber-300 px-2.5 py-1.5 rounded-md"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
            <table className="w-full text-sm" data-testid="sessions-log">
              <thead className="text-gray-500 text-[10px] uppercase tracking-[0.2em] bg-black/30 sticky top-0">
                <tr>
                  {!selectedTableId && (
                    <th className="text-left px-5 py-3">Table</th>
                  )}
                  <th className="text-left px-5 py-3">Player</th>
                  <th className="text-left px-5 py-3">Start Time</th>
                  <th className="text-left px-5 py-3">Stop Time</th>
                  <th className="text-right px-5 py-3">Collected</th>
                  <th className="text-left px-5 py-3">Payment Method</th>
                </tr>
              </thead>
              <tbody>
                {filteredSessions.map((s) => (
                  <tr
                    key={s.id}
                    className="border-t border-white/5 hover:bg-white/[0.02]"
                    data-testid={`session-row-${s.id}`}
                  >
                    {!selectedTableId && (
                      <td className="px-5 py-3 font-bold">{s.table_name}</td>
                    )}
                    <td className="px-5 py-3">
                      <span className="font-bold text-white">
                        {s.player_name}
                      </span>{" "}
                      <span
                        className={`ml-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                          s.player_type === "member"
                            ? "bg-amber-400/15 text-amber-200"
                            : s.player_type === "cross_club_member"
                            ? "bg-sky-400/15 text-sky-200"
                            : "bg-white/10 text-gray-300"
                        }`}
                      >
                        {s.player_type === "member" ? "M" : s.player_type === "cross_club_member" ? "CCM" : "NM"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-gray-400 font-mono text-xs">
                      {fmt(s.start_time)}
                    </td>
                    <td className="px-5 py-3 text-gray-400 font-mono text-xs">
                      {fmt(s.end_time)}
                    </td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums text-amber-300 font-bold">
                      {INR(s.amount)}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs uppercase tracking-wider text-gray-200">
                          {s.payment_mode || "—"}
                        </span>
                        {s.payment_status === "paid" ? (
                          <span className="inline-flex items-center gap-1 text-emerald-300 text-[10px] uppercase font-bold tracking-wider">
                            <CheckCircle2 className="w-3 h-3" /> Paid
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-rose-300 text-[10px] uppercase font-bold tracking-wider">
                            <XCircle className="w-3 h-3" /> Unpaid
                          </span>
                        )}
                        {s.email_status === "sent" && (
                          <span
                            className="inline-flex items-center gap-1 text-emerald-400/80 text-[10px] uppercase font-bold tracking-wider"
                            title={`Receipt sent ${s.email_sent_at || ""}`}
                          >
                            <Mail className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredSessions.length === 0 && (
                  <tr>
                    <td
                      colSpan={selectedTableId ? 5 : 6}
                      className="text-center text-gray-500 py-8 text-sm"
                    >
                      {loading
                        ? "Loading…"
                        : selectedTableId
                          ? `No sessions for ${selectedTableName} in this window`
                          : "No sessions in this window"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer totals for the current filter */}
          {filteredSessions.length > 0 && (
            <div className="px-5 py-3 border-t border-white/5 bg-black/20 flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="text-gray-500 uppercase tracking-widest font-bold">
                {selectedTableName} · {filteredSessions.length} session{filteredSessions.length === 1 ? "" : "s"}
              </div>
              <div className="flex items-center gap-5">
                <div>
                  <span className="text-gray-500 uppercase tracking-widest mr-2">Total Collected</span>
                  <span className="text-amber-300 font-mono font-bold tabular-nums">
                    {INR(
                      filteredSessions
                        .filter((s) => s.payment_status === "paid")
                        .reduce((a, s) => a + (Number(s.amount) || 0), 0)
                    )}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 uppercase tracking-widest mr-2">Total Time</span>
                  <span className="text-emerald-300 font-mono font-bold tabular-nums">
                    {hours(
                      filteredSessions.reduce((a, s) => a + (Number(s.duration_minutes) || 0), 0)
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Payment mode split */}
        {overview?.by_payment_mode?.length > 0 && (
          <section className="bg-[#121a16] border border-white/10 rounded-2xl p-5">
            <h3 className="text-sm uppercase tracking-[0.25em] text-gray-300 font-bold mb-3">
              Payment Mode Split
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {overview.by_payment_mode.map((m) => (
                <div
                  key={m.mode}
                  className="bg-black/40 border border-white/10 rounded-lg px-4 py-3"
                >
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">
                    {m.mode}
                  </div>
                  <div className="text-xl font-black mt-1 text-amber-300 font-mono tabular-nums">
                    {INR(m.amount)}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">
                    {m.sessions} session{m.sessions === 1 ? "" : "s"}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Unpaid sessions — owner can mark paid (mirrors what Admin sees) */}
        <div id="unpaid-sessions-anchor">
          <UnpaidSessionsPanel
            refreshSignal={unpaidRefresh}
            onChanged={() => {
              setUnpaidRefresh((v) => v + 1);
              // Also refresh analytics so the dashboard KPIs reflect the change.
              refresh();
            }}
          />
        </div>

        {/* Players Portal — placed at the end of the Owner page per request */}
        <PlayersPortalCard />
      </main>

      {/* Revenue drilldown */}
      <Dialog open={revenueDrill} onOpenChange={setRevenueDrill}>
        <DialogContent
          data-testid="revenue-drilldown"
          className="bg-[#121a16] border border-amber-400/30 text-white max-w-2xl"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <Coins className="w-5 h-5 text-amber-400" />
              Revenue by Table
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {appliedRange.start} → {appliedRange.end} · Total collected{" "}
              <b className="text-amber-300">{INR(totalCollected)}</b>
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2">
            <table className="w-full text-sm">
              <thead className="text-gray-500 text-[10px] uppercase tracking-[0.2em] bg-black/40 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Table</th>
                  <th className="text-right px-3 py-2">Sessions</th>
                  <th className="text-right px-3 py-2">Collected</th>
                  <th className="text-right px-3 py-2">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {tablesByRevenue.map((t) => {
                  const pct = totalCollected
                    ? (t.revenue_collected / totalCollected) * 100
                    : 0;
                  return (
                    <tr
                      key={t.table_id}
                      className="border-t border-white/5"
                      data-testid={`drill-rev-${t.table_id}`}
                    >
                      <td className="px-3 py-2 font-bold">{t.table_name}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {t.sessions}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-amber-300 font-bold">
                        {INR(t.revenue_collected)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <div className="w-24 h-1.5 bg-white/10 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-amber-400"
                              style={{ width: `${Math.min(100, pct)}%` }}
                            />
                          </div>
                          <span className="font-mono text-xs text-gray-400 w-12 text-right">
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {tables.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-3 py-6 text-center text-gray-500"
                    >
                      No data
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Play-time drilldown */}
      <Dialog open={playtimeDrill} onOpenChange={setPlaytimeDrill}>
        <DialogContent
          data-testid="playtime-drilldown"
          className="bg-[#121a16] border border-emerald-400/30 text-white max-w-2xl"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <Clock className="w-5 h-5 text-emerald-400" />
              Play Time by Table
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {appliedRange.start} → {appliedRange.end} · Total{" "}
              <b className="text-emerald-300">{hours(totalMinutes)}</b>
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2">
            <table className="w-full text-sm">
              <thead className="text-gray-500 text-[10px] uppercase tracking-[0.2em] bg-black/40 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Table</th>
                  <th className="text-right px-3 py-2">Sessions</th>
                  <th className="text-right px-3 py-2">Play Time</th>
                  <th className="text-right px-3 py-2">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {tablesByMinutes.map((t) => {
                  const pct = totalMinutes
                    ? (t.total_minutes / totalMinutes) * 100
                    : 0;
                  return (
                    <tr
                      key={t.table_id}
                      className="border-t border-white/5"
                      data-testid={`drill-time-${t.table_id}`}
                    >
                      <td className="px-3 py-2 font-bold">{t.table_name}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {t.sessions}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-300 font-bold">
                        {hours(t.total_minutes)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <div className="w-24 h-1.5 bg-white/10 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-400"
                              style={{ width: `${Math.min(100, pct)}%` }}
                            />
                          </div>
                          <span className="font-mono text-xs text-gray-400 w-12 text-right">
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {tables.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-3 py-6 text-center text-gray-500"
                    >
                      No data
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
      {/* Share report modal */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent
          data-testid="email-report-dialog"
          className="bg-[#121a16] border border-blue-400/30 text-white max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-400" />
              Share Excel Report
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Downloads the Excel and opens your chosen app pre-filled. No API
              keys needed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Send Via
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "gmail", label: "Gmail" },
                  { id: "outlook", label: "Outlook" },
                  { id: "yahoo", label: "Yahoo" },
                  { id: "whatsapp", label: "WhatsApp" },
                  { id: "mailto", label: "Default Mail" },
                ].map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setEmailForm({ ...emailForm, channel: c.id })}
                    data-testid={`share-channel-${c.id}`}
                    className={`px-2 py-2 text-[11px] uppercase tracking-widest font-bold rounded border transition ${
                      emailForm.channel === c.id
                        ? "border-blue-400 bg-blue-500/20 text-blue-200"
                        : "border-white/10 text-gray-300 hover:border-blue-400/50"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Recipient (optional)
              </Label>
              <Input
                data-testid="email-to-input"
                type={emailForm.channel === "whatsapp" ? "tel" : "email"}
                placeholder={
                  emailForm.channel === "whatsapp"
                    ? "Leave blank to pick contact in WhatsApp"
                    : user?.email
                }
                value={emailForm.to}
                onChange={(e) =>
                  setEmailForm({ ...emailForm, to: e.target.value })
                }
                className="bg-black/40 border-white/10 text-white"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Note (added to message)
              </Label>
              <Input
                data-testid="email-note-input"
                value={emailForm.note}
                onChange={(e) =>
                  setEmailForm({ ...emailForm, note: e.target.value })
                }
                placeholder="e.g. Monthly report for review"
                className="bg-black/40 border-white/10 text-white"
              />
            </div>

            <div className="bg-blue-500/10 border border-blue-400/30 rounded-md px-3 py-2 text-xs text-blue-200 leading-relaxed">
              <b className="text-blue-100">Steps:</b> Click <b>Share</b> →
              the Excel downloads + your chosen app opens in a new tab with
              subject &amp; body pre-filled → <b>attach the downloaded file</b>{" "}
              from your downloads folder &amp; send.
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button
              variant="ghost"
              onClick={() => setEmailOpen(false)}
              className="text-gray-400 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={shareReport}
              disabled={emailing}
              data-testid="confirm-email-btn"
              className="bg-blue-500 hover:bg-blue-600 text-white font-black uppercase tracking-widest"
            >
              {emailing ? "Preparing…" : "Share"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Staff password reveal dialog — shown once after a reset */}
      <Dialog open={!!staffReveal} onOpenChange={(v) => !v && setStaffReveal(null)}>
        <DialogContent
          data-testid="staff-reveal-dialog"
          className="bg-[#121a16] border border-amber-400/40 text-white max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-400" />
              New staff password
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Save this somewhere safe — for security, it won&apos;t be shown again.
            </DialogDescription>
          </DialogHeader>
          {staffReveal && (
            <div className="space-y-3">
              <div className="bg-black/40 border border-white/10 rounded-md px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
                  Email
                </div>
                <div className="font-mono text-white text-sm break-all">
                  {staffReveal.email}
                </div>
              </div>
              <div className="bg-black/40 border border-amber-400/40 rounded-md px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
                  New password
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div
                    data-testid="staff-reveal-password"
                    className="font-mono text-amber-200 text-base break-all select-all"
                  >
                    {staffReveal.new_password}
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = await safeCopy(staffReveal.new_password);
                      if (ok) {
                        toast.success("Copied to clipboard");
                      } else {
                        toast.error(
                          "Couldn't copy automatically — long-press the password above to select and copy manually.",
                        );
                      }
                    }}
                    data-testid="staff-reveal-copy"
                    className="text-[10px] uppercase tracking-widest font-bold text-amber-300 hover:text-amber-100 border border-amber-400/40 px-2.5 py-1.5 rounded-md"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end pt-2">
            <Button
              onClick={() => setStaffReveal(null)}
              className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeeTile({ label, v, sub, accent }) {
  const accentCls = accent === "amber" ? "text-amber-300" : "text-white";
  return (
    <div className="bg-black/40 border border-white/10 rounded-lg p-3 min-h-[68px]">
      <div className="text-[10px] uppercase tracking-widest text-gray-500">
        {label}
      </div>
      <div className={`text-base font-black mt-1 tabular-nums ${accentCls}`}>
        {v}
      </div>
      {sub && (
        <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">
          {sub}
        </div>
      )}
    </div>
  );
}

